<template>
  <div class="card card-absolute rfo-card rfo-card--primary oa-section-card">
    <div class="card-header bg-secondary text-white">
      <h5 class="mb-0"><i class="fa fa-clock-o me-2"></i>Prepare The Invoice In Odoo</h5>
    </div>
    <div class="card-body">
      <div class="alert alert-info outline small mb-3">
        <i class="fa fa-info-circle me-1"></i>
        We checked Odoo twice: it holds no invoice entry for this order, and no record of ever
        having been asked for one. Click below to ask Odoo to prepare it. Odoo usually responds
        within 30 seconds but it can take a little longer.
      </div>

      <div v-if="phase === 'idle'">
        <button class="btn btn-primary text-white" :disabled="isTriggering" @click="onTrigger">
          <span v-if="isTriggering">
            <span class="spinner-border spinner-border-sm me-1"></span>Triggering…
          </span>
          <span v-else><i class="fa fa-bolt me-1"></i>Ask Odoo To Prepare The Invoice</span>
        </button>
      </div>

      <div v-else-if="phase === 'polling'">
        <div class="d-flex align-items-center mb-3">
          <span class="spinner-border text-primary me-3"></span>
          <div>
            <strong>Waiting for Odoo to be ready…</strong>
            <div class="text-muted small">
              Elapsed {{ formatElapsed(elapsedMs) }} / max {{ formatElapsed(currentTimeoutMs) }} ·
              polling every 5 s
            </div>
          </div>
        </div>
        <div class="d-flex gap-2">
          <button class="btn btn-outline-primary btn-sm" @click="onRefreshNow">
            <i class="fa fa-refresh me-1"></i>Check now
          </button>
          <button class="btn btn-outline-secondary btn-sm" @click="onCancel">
            <i class="fa fa-times me-1"></i>Stop waiting
          </button>
        </div>
      </div>

      <div v-else-if="phase === 'timeout'">
        <div class="alert alert-warning outline small mb-3">
          <i class="fa fa-exclamation-triangle me-1"></i>
          Still waiting after {{ formatElapsed(elapsedMs) }}. Odoo has neither created the invoice
          entry nor refused the request.
        </div>
        <div v-if="escalateThreshold" class="alert alert-danger outline small mb-3">
          <i class="fa fa-phone me-1"></i>
          This has been waiting for over 5 minutes. Something may be stuck — reach out to the
          operations lead.
        </div>
        <div class="d-flex gap-2">
          <button class="btn btn-warning btn-sm" @click="onExtend">
            <i class="fa fa-clock-o me-1"></i>Keep waiting (+90 s)
          </button>
          <button class="btn btn-outline-secondary btn-sm" @click="onCancel">
            <i class="fa fa-times me-1"></i>Stop
          </button>
        </div>
      </div>

      <div v-if="error" class="alert alert-danger outline small mt-3 mb-0">
        <i class="fa fa-exclamation-triangle me-1"></i>{{ error }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue';
import { useToast } from 'vue-toastification';
import { apiClient, API_PATHS } from '@/services/api';
import { useQueueMessagePoll } from '@/services/ryd-pay-operations/order-amendments/use-queue-message-poll';

const props = defineProps({
  uuid: { type: String, required: true },
  /**
   * kw.api.log id of a rejection already known before triggering, so a pre-existing one does
   * not abort a poll that is still legitimately waiting.
   */
  seenRejectionLogId: { type: Number, default: null },
});

const emit = defineEmits(['found', 'rejected', 'cancelled', 'audit']);

const toast = useToast();
const isTriggering = ref(false);
const phase = ref('idle');
const error = ref('');
const escalateThreshold = ref(false);
const currentTimeoutMs = ref(90_000);

const { isPolling, elapsedMs, rejection, start, extend, refreshNow, cancel } =
  useQueueMessagePoll();

watch(elapsedMs, (val) => {
  if (val > 5 * 60 * 1000) {
    escalateThreshold.value = true;
  }
});

async function onTrigger() {
  isTriggering.value = true;
  error.value = '';
  try {
    await apiClient.rpIntegration.post(
      API_PATHS.rpIntegration.payOperations.orderAmendments.triggerInvoiceGeneration(props.uuid),
    );
  } catch (err) {
    error.value =
      err?.response?.data?.message ?? err?.message ?? 'Failed to ask Odoo to prepare the invoice.';
    toast.error(error.value);
  } finally {
    emit('audit');
  }
  isTriggering.value = false;

  phase.value = 'polling';
  currentTimeoutMs.value = 90_000;
  const result = await start(props.uuid, {
    intervalMs: 5_000,
    timeoutMs: 90_000,
    seenRejectionLogId: props.seenRejectionLogId,
  });
  settle(result);
}

async function onExtend() {
  phase.value = 'polling';
  currentTimeoutMs.value += 90_000;
  settle(await extend(90_000));
}

/**
 * Three outcomes, not two: Odoo produced the entry, Odoo refused the request, or neither
 * happened within the budget. Only the last one is a timeout.
 */
function settle(result) {
  if (result) {
    emit('found', result);
    return;
  }
  if (rejection.value) {
    phase.value = 'idle';
    emit('rejected', rejection.value);
    return;
  }
  if (isPolling.value === false) {
    // Either timed out or was cancelled.
    phase.value = error.value ? 'idle' : 'timeout';
  }
}

async function onRefreshNow() {
  settle(await refreshNow());
}

function onCancel() {
  cancel();
  emit('cancelled');
}
watch(
  () => props.uuid,
  () => {
    cancel();
    phase.value = 'idle';
    error.value = '';
    escalateThreshold.value = false;
  },
);

function formatElapsed(ms) {
  const totalSec = Math.floor(ms / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return `${min}:${String(sec).padStart(2, '0')}`;
}
</script>
